import { useCallback, useEffect, useRef, useState } from 'react';
import { EXTERNAL_NAME_STORAGE_KEY, MEET_BOOTSTRAP_NAME_STORAGE_KEY, IN_ROOM, LANGUAGE_STORAGE_KEY } from 'consts';
import { callPolicyStore, roomInfoStore } from 'state/store';
import { getLanguage } from '@web-chat-core/ui-kit';
import { usePrepareMeet } from 'useCase/app/usePrepareMeet';
import { useUserSettingsContext } from 'contexts';
import { useSIPTelephony } from 'useCase';
import { applyCallSessionPatch, clearInRoomFlag, clearWaitingQueue } from 'features/PreJoin/utils/waitingRoom/session';
import {
    isMeetBootstrapFromQuery,
    isMeetBootstrapSessionForRoom,
    persistMeetBootstrapFromQuery,
    stripMeetBootstrapQueryFromUrl,
} from 'features/PreJoin/utils/bootstrap';
import { consumePageReloading } from 'features/AccessRequests/utils/canBypassWaitingRoom';
import { restoreCallPolicyDraftFromSession } from 'features/PreJoin/utils/policy';
import { getCallLsPolicy } from 'features/PreJoin/utils/callLsData';
import { getNameFromToken } from '../../helpers';

export const useBootstrapMeetSession = (roomName: string) => {
    const { getStartQueryParams } = usePrepareMeet();
    const { setVideoEnabled } = useUserSettingsContext();
    const { isSIPOn, prepareSIPConnect } = useSIPTelephony();

    const [showLoader, setShowLoader] = useState(true);

    const {
        token,
        wssUrl,
        fullName,
        language,
        isP2P,
        isSIP,
        soundEnabledParam,
        notificationsEnabledParam,
        joinApprovalPolicy,
        initiatorId: initiatorIdParam,
        callId: callIdParam,
        workspaceId: workspaceIdParam,
    } = getStartQueryParams();

    const prepareDataAndStartRoom = useCallback(() => {
        if (!sessionStorage.getItem(IN_ROOM)) {
            roomInfoStore.clearMetadata();
        }

        const lang = language || getLanguage();
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);

        const isBootstrapFromUrl = isMeetBootstrapFromQuery({
            initiatorId: initiatorIdParam,
            callId: callIdParam,
            workspaceId: workspaceIdParam,
            fullName,
        });
        const isBootstrap = isBootstrapFromUrl || isMeetBootstrapSessionForRoom(roomName);
        if (isBootstrapFromUrl) {
            persistMeetBootstrapFromQuery(roomName, {
                initiatorId: initiatorIdParam,
                callId: callIdParam,
                workspaceId: workspaceIdParam,
                fullName,
                soundEnabledParam,
                notificationsEnabledParam,
                joinApprovalPolicy,
            });
        } else if (isBootstrap && !isP2P) {
            restoreCallPolicyDraftFromSession();
        }

        if (!isBootstrap && !isP2P) {
            callPolicyStore.reset();
        }

        // Fallback из localStorage: sessionStorage.clear() в webEntry уничтожает все данные при F5,
        // поэтому если после всей bootstrap-логики isBootstrap = false (session пуста),
        // берём последнее известное значение привязанное к этой комнате.
        // Если roomName не совпадает - getCallLsPolicy вернёт null и очистит устаревшие данные.
        // Не применяем если пришли из URL-bootstrap (политика уже установлена из URL).
        if (!isBootstrapFromUrl && !isBootstrap && !isP2P) {
            const lsPolicy = getCallLsPolicy(roomName);
            if (lsPolicy) {
                callPolicyStore.set(lsPolicy);
            }
        }

        if (consumePageReloading()) {
            // F5: выпадаем из очереди, снова в waiting room только по кнопке Подключиться
            clearWaitingQueue();
        }

        // Web start: сначала сбрасываем IN_ROOM (вкладка могла переоткрыться), потом применяем параметры URL.
        clearInRoomFlag();
        applyCallSessionPatch({
            initiatorId: isBootstrap ? initiatorIdParam ?? undefined : undefined,
            callId: isBootstrap ? callIdParam ?? undefined : undefined,
            workspaceId: isBootstrap ? workspaceIdParam ?? undefined : undefined,
            token: isBootstrap ? token : null,
            wssUrl: isBootstrap ? wssUrl : null,
        });

        if (isBootstrap && token && wssUrl) {
            const resolvedName = getNameFromToken(token);
            sessionStorage.setItem(MEET_BOOTSTRAP_NAME_STORAGE_KEY, resolvedName);
            localStorage.setItem(EXTERNAL_NAME_STORAGE_KEY, resolvedName);

            if (isP2P) setVideoEnabled(false);
            if (isSIP && isSIPOn()) prepareSIPConnect();
        } else if (isBootstrap && fullName) {
            sessionStorage.setItem(MEET_BOOTSTRAP_NAME_STORAGE_KEY, fullName);
            localStorage.setItem(EXTERNAL_NAME_STORAGE_KEY, fullName);
        }

        stripMeetBootstrapQueryFromUrl();

        setShowLoader(false);
    }, [
        token,
        wssUrl,
        fullName,
        language,
        isP2P,
        isSIP,
        soundEnabledParam,
        notificationsEnabledParam,
        joinApprovalPolicy,
        initiatorIdParam,
        callIdParam,
        workspaceIdParam,
        roomName,
        setVideoEnabled,
        prepareSIPConnect,
        isSIPOn,
    ]);

    const bootstrappedRoomRef = useRef<string | null>(null);

    useEffect(() => {
        if (bootstrappedRoomRef.current === roomName) {
            return;
        }
        bootstrappedRoomRef.current = roomName;
        prepareDataAndStartRoom();
    }, [roomName, prepareDataAndStartRoom]);

    return { showLoader, prepareDataAndStartRoom };
};
